<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            class="lockout-audit-report"
            grid-theme="ag-theme-alpine-report"
        >
            <responsive-button
                :href="exportExcelHref"
                :icon="['fas', 'file-excel']"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            />
             <exportToPDFButton
                :pdfCacheKey="pdfCacheKey"
                :href="exportPdfHref"
                :pageOrientation="pageOrientation.toLowerCase()"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="pageOrientationOptions"
                variant="primary"
            >
                {{ pageOrientation }}
            </responsive-dropdown>
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import { REPORT_LOCKOUT_AUDIT_ENDPOINT } from '../../endpoints';
import ReportSummaryBar from './report-summary-bar.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { PAGE_ORIENTATION_LABELS } from '../../constants'
import { defineAsyncComponent } from 'vue';
const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    name: 'ReportLockoutAudit',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        ResponsiveDropdown,
        exportToPDFButton,
        reportSummaryBar: ReportSummaryBar,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Lockout Audit Log',
            tabs: [
                {
                    title: 'Lockout Audit Log',
                    route: '/app/reports/lockout-audit/',
                    active: true,
                },
            ],
            busy: false,
            gridApi: null,
            rowData: [],
            windowWidth: 0,
            windowHeight: 0,
            pageOrientation: PAGE_ORIENTATION_LABELS[0],
            statusBar: null,
            pdfCacheKey: null,
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat', 
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned ? this.rowData.length : params.node.rowIndex + 1
                    },
                    minWidth: 40,
                },
                {
                    field: 'invoice_id',
                    headerName: 'Invoice #',
                    minWidth: 80,
                },
                {
                    field: 'property',
                    headerName: 'Property',
                    minWidth: 100,
                },
                {
                    field: 'from_date',
                    headerName: 'From Date',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'to_date',
                    headerName: 'To Date',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'conflict_id',
                    headerName: 'Conflict #',
                    minWidth: 80,
                },
                {
                    field: 'c_property',
                    headerName: 'C.Property',
                    minWidth: 100,
                },
                {
                    field: 'c_from_date',
                    headerName: 'C.From Date',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'c_to_date',
                    headerName: 'C.To Date',
                    type: 'dateColumn',
                    minWidth: 100,
                    autoWidthExclude: true
                },
            ];
        },
        pdfColumnWidths() {
            return [
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                '*',
            ];
        },
        pageOrientationOptions() {
            return [
                {
                    label: PAGE_ORIENTATION_LABELS[0],
                    click: () => { this.pageOrientation = PAGE_ORIENTATION_LABELS[0] },
                },
                {
                    label: PAGE_ORIENTATION_LABELS[1],
                    click: () => { this.pageOrientation = PAGE_ORIENTATION_LABELS[1] },
                },
            ]
        },
        exportPdfHref() {
            return '/app/reports/lockout-audit/row-data/pdf/';
        },
        exportExcelHref() {
            return '/app/reports/lockout-audit/row-data/excel/';
        },
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
    },
    mounted() {
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() { 
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_LOCKOUT_AUDIT_ENDPOINT(),
                );
                this.rowData = data.items;
                this.pdfCacheKey = data.pdf_cache_key
            } catch (e) {
                this.errorToast({ text: "Could not fetch data" });
            } finally {
                this.busy = false
            }
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance = statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
    },
};
</script>
